import { useCallback, useRef } from 'react';
import { isCtiEmbed, AuthBootstrapContext } from '../../services/sap-context';
import { resolveAuthBootstrapContext } from '../../services/sap-context';
import { validateRequiredAppConfig } from '../../utils/config';
import { logger } from '../../utils/logger';
import { mapAuthError } from './useAuthTokenLifecycle';
import {
  tryValidateExistingSession,
  resolveAuthContextOrThrow,
} from './auth-bootstrap-session.util';

interface AuthBootstrapHandlers {
  setLoading: () => void;
  setBlockedOutsideSap: () => void;
  setAuthenticated: (
    accessToken: string,
    expiresIn: number,
    user: import('../../services/api').AuthUser,
    sessionToken?: string
  ) => void;
  setNeedsNiceLink: (error: string | null) => void;
  setFatalError: (error: string) => void;
}

export function useAuthBootstrap(handlers: AuthBootstrapHandlers) {
  const authBootstrapRef = useRef<AuthBootstrapContext | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const bootstrapStartedRef = useRef(false);

  const resolveAuthContext = useCallback(async (): Promise<AuthBootstrapContext | null> => {
    const authContext = await resolveAuthBootstrapContext();
    authBootstrapRef.current = authContext;
    return authContext;
  }, []);

  const promptNiceLink = useCallback(
    async (error: string | null = null) => {
      await resolveAuthContext();
      handlersRef.current.setNeedsNiceLink(error);
    },
    [resolveAuthContext]
  );

  const bootstrapAuth = useCallback(async () => {
    if (bootstrapStartedRef.current) {
      return;
    }
    bootstrapStartedRef.current = true;

    handlersRef.current.setLoading();

    try {
      validateRequiredAppConfig();
    } catch (err) {
      logger.error('[Auth] Invalid application setup:', err);
      handlersRef.current.setFatalError(mapAuthError(err));
      return;
    }

    if (!isCtiEmbed()) {
      handlersRef.current.setBlockedOutsideSap();
      return;
    }

    const validateOutcome = await tryValidateExistingSession(handlersRef.current);
    if (validateOutcome.status === 'authenticated') {
      return;
    }

    try {
      if (validateOutcome.status === 'needs_nice_link') {
        await promptNiceLink(validateOutcome.error);
        return;
      }

      const authContext = await resolveAuthContextOrThrow();
      authBootstrapRef.current = authContext;
      handlersRef.current.setNeedsNiceLink(null);
    } catch (err) {
      logger.error('[Auth] Authentication bootstrap failed:', err);
      handlersRef.current.setFatalError(mapAuthError(err));
    }
  }, [promptNiceLink]);

  return {
    authBootstrapRef,
    resolveAuthContext,
    bootstrapAuth,
  };
}
